// src/services/image.ts
// ============================================
// 图片生成服务 - 对接多米 API（异步模式）
//
// 接口地址：https://duomiapi.com/v1/images/generations?async=true
// API 密钥：通过环境变量 DUOMI_API_KEY 配置（或使用默认值）
//
// 重要：多米 API 已切换为异步模式
//   1. 创建任务：POST /v1/images/generations?async=true  → 返回 {id}
//   2. 轮询查询：GET /v1/tasks/{id}                       → 返回 {state, data.images[].url}
//
// 支持的模型：gpt-image-2（仅此一个，dall-e-3 已被该 API Key 拒绝）
// 支持的尺寸：1024x1024 / 1792x1024 / 1024x1792
//
// 浏览器端通过 vite 代理 /duomi-api 调用，避免 CORS
// ============================================

/** 图片生成请求参数 */
export interface ImageGenerationRequest {
  /** 提示词（详细描述要生成的图片） */
  prompt: string;
  /** 模型名称（默认 gpt-image-2，多米 API 当前仅支持该模型） */
  model?: string;
  /** 图片数量（默认 1） */
  n?: number;
  /** 图片尺寸（默认 1024x1024） */
  size?: '256x256' | '512x512' | '1024x1024' | '1792x1024' | '1024x1792';
  /** 风格（保留字段，gpt-image-2 不支持，仅用于兼容旧调用方） */
  style?: 'vivid' | 'natural';
  /** 质量（保留字段，gpt-image-2 不支持，仅用于兼容旧调用方） */
  quality?: 'standard' | 'hd';
  /** 返回格式（保留字段，异步模式固定返回 URL，仅用于兼容旧调用方） */
  response_format?: 'url' | 'b64_json';
}

/** 图片生成结果 */
export interface ImageGenerationResult {
  /** 是否成功 */
  success: boolean;
  /** 生成的图片 URL 或 base64 数据 */
  images: Array<{
    url?: string;
    b64Json?: string;
    revisedPrompt?: string;
  }>;
  /** 生成耗时（毫秒） */
  durationMs: number;
  /** 错误信息 */
  error?: string;
  /** 使用的模型 */
  model?: string;
}



/** 浏览器端 API 基础路径（通过 vite 代理） */
const BROWSER_API_BASE = '/duomi-api/v1';

/** Node 端 API 基础路径（直连） */
const NODE_API_BASE = 'https://duomiapi.com/v1';

/** 获取 API 基础路径（自动判断环境，支持热插拔配置） */
function getApiBase(): string {
  // 优先从热插拔配置读取
  try {
    const { apiConfig } = require('../services/apiConfig');
    const config = apiConfig.get('duomi');
    if (config && config.apiBase) {
      return config.apiBase;
    }
  } catch {
    // 降级
  }
  if (typeof window !== 'undefined') {
    return BROWSER_API_BASE;
  }
  return NODE_API_BASE;
}

/**
 * 获取 API 密钥（支持热插拔配置）
 * 安全说明：图片生成经 /duomi-api 代理，后端统一代填多米密钥并覆盖客户端 Authorization，
 * 前端不再硬编码密钥，避免泄漏到 bundle。
 */
function getApiKey(): string {
  // 优先从热插拔配置读取
  try {
    const { apiConfig } = require('../services/apiConfig');
    const config = apiConfig.get('duomi');
    if (config && config.apiKey) {
      return config.apiKey;
    }
  } catch {
    // 降级
  }
  // 优先从环境变量读取（Node 环境）
  if (typeof process !== 'undefined' && process.env?.DUOMI_API_KEY) {
    return process.env.DUOMI_API_KEY;
  }
  // 浏览器环境由后端代填，前端不持有密钥
  return '';
}

/**
 * 图片生成服务
 *
 * 核心方法：
 *   - generateImage()：单次生成图片（异步轮询模式）
 *   - generateCover()：生成文章封面（预设竖版 1024x1792）
 *   - generateWeChatCover()：生成公众号配图（横版 1792x1024）
 *   - generateXiaohongshuGrid()：生成小红书九宫格（9 张方图）
 *   - generateDouyinCover()：生成抖音封面（竖版 1024x1792）
 *
 * 重要：所有方法都使用 gpt-image-2 模型（多米 API 当前仅支持此模型）
 */
export class ImageGenerationService {
  private logger: Console;
  private apiKey: string;
  private apiBase: string;

  constructor(logger: Console = console) {
    this.logger = logger;
    this.apiKey = getApiKey();
    this.apiBase = getApiBase();
  }

  /**
   * 查询异步任务结果
   * 多米 API 任务状态：pending / running / succeeded / error
   *
   * 注意：gpt-image-2 实际生成耗时约 1-10 分钟（视 API 排队情况），高峰排队可能 20 分钟+。
   * v6.14：超时从 12 分钟加长到 30 分钟（用户实测"后台生成成功但前端报失败"=排队超时导致）
   */
  private async pollTask(taskId: string, totalTimeoutMs: number = 1800000): Promise<{ success: boolean; imageUrl?: string; error?: string; state?: string }> {
    const pollIntervalMs = 5000; // 每 5 秒查询一次（避免过于频繁）
    const maxIterations = Math.ceil(totalTimeoutMs / pollIntervalMs);

    for (let i = 0; i < maxIterations; i++) {
      try {
        const resp = await fetch(`${this.apiBase}/tasks/${taskId}`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${this.apiKey}`,
          },
        });

        if (!resp.ok) {
          const errText = await resp.text().catch(() => '');
          // 404/401/400：任务ID无效或鉴权失败，直接退出；5xx 等暂时性错误：后台任务仍在继续，继续轮询（v6.13 修复"后台生成OK但前端失败"）
          if (resp.status === 404 || resp.status === 401 || resp.status === 400) {
            return { success: false, error: `查询任务失败: API 返回 ${resp.status}: ${errText || resp.statusText}` };
          }
          this.logger.warn(`[ImageGenerationService] 查询任务暂时失败（${resp.status}），继续轮询（第 ${i + 1} 次）`);
          await new Promise((r) => setTimeout(r, pollIntervalMs));
          continue;
        }

        const data = await resp.json();
        const state = data?.state || data?.status || '';
        const progress = data?.progress ?? 0;
        const elapsedSec = i * pollIntervalMs / 1000;

        this.logger.info(`[ImageGenerationService] 任务 ${taskId} 状态: ${state} 进度: ${progress}% 已等待: ${elapsedSec}s`);

        if (state === 'succeeded' || state === 'success') {
          // 成功：从 data.images 数组取第一个 url
          const images = data?.data?.images || data?.images || [];
          if (images.length > 0 && images[0].url) {
            return { success: true, imageUrl: images[0].url, state };
          }
          return { success: false, error: '任务成功但未返回图片URL', state };
        }

        if (state === 'error' || state === 'failed') {
          const errMsg = data?.data?.description || data?.error || '任务执行失败';
          return { success: false, error: errMsg, state };
        }

        // pending / running：继续轮询
        await new Promise((r) => setTimeout(r, pollIntervalMs));
      } catch (err: any) {
        // 网络异常：等待后重试
        this.logger.warn(`[ImageGenerationService] 查询任务异常（第 ${i + 1} 次）: ${err?.message}`);
        await new Promise((r) => setTimeout(r, pollIntervalMs));
      }
    }

    return { success: false, error: `任务在 ${totalTimeoutMs / 1000}s 内未完成（可能仍在后台排队生成）。任务ID: ${taskId}，请到多米 API 控制台确认最终结果，若已生成可稍后重试查询` };
  }

  /**
   * 生成图片（核心方法 - 异步轮询模式）
   *
   * 流程：
   *   1. POST /v1/images/generations?async=true 创建任务，拿到 {id}
   *   2. 轮询 GET /v1/tasks/{id} 直到 state=succeeded
   *   3. 返回 data.images[0].url
   *
   * @param request 生成参数
   * @returns 生成结果
   */
  async generateImage(request: ImageGenerationRequest): Promise<ImageGenerationResult> {
    const startTime = Date.now();
    const model = request.model || 'gpt-image-2';

    try {
      // gpt-image-2 模型请求体（多米异步模式只接受 model/prompt/n/size 字段）
      const body: Record<string, unknown> = {
        model,
        prompt: request.prompt,
        n: request.n || 1,
        size: request.size || '1024x1024',
      };

      this.logger.info(`[ImageGenerationService] 提交生图任务（异步）: ${model} / ${body.size}`);

      // 步骤1：创建任务（必须带 ?async=true 参数）
      const createResp = await fetch(`${this.apiBase}/images/generations?async=true`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${this.apiKey}`,
        },
        body: JSON.stringify(body),
      });

      if (!createResp.ok) {
        const errText = await createResp.text().catch(() => '');
        return {
          success: false,
          images: [],
          durationMs: Date.now() - startTime,
          error: `API 返回 ${createResp.status}: ${errText || createResp.statusText}`,
          model,
        };
      }

      const createData = await createResp.json();
      const taskId = createData?.id;
      if (!taskId) {
        // 兼容：部分旧版API可能直接返回 data 数组（同步模式）
        const directImages = (createData?.data || []).map((item: any) => ({
          url: item.url,
          b64Json: item.b64_json,
          revisedPrompt: item.revised_prompt,
        }));
        if (directImages.length > 0) {
          return {
            success: true,
            images: directImages,
            durationMs: Date.now() - startTime,
            model,
          };
        }
        return {
          success: false,
          images: [],
          durationMs: Date.now() - startTime,
          error: `任务创建失败：响应缺少 id 字段。响应: ${JSON.stringify(createData).slice(0, 200)}`,
          model,
        };
      }

      this.logger.info(`[ImageGenerationService] 任务已创建，ID: ${taskId}，开始轮询结果...`);

      // 步骤2：轮询查询任务结果
      const pollResult = await this.pollTask(taskId);
      const durationMs = Date.now() - startTime;

      if (!pollResult.success) {
        return {
          success: false,
          images: [],
          durationMs,
          error: pollResult.error || '生成失败',
          model,
        };
      }

      return {
        success: true,
        images: [{ url: pollResult.imageUrl }],
        durationMs,
        model,
      };
    } catch (err: any) {
      return {
        success: false,
        images: [],
        durationMs: Date.now() - startTime,
        error: err?.message || String(err),
        model,
      };
    }
  }

  /**
   * v6.1.12 图生图（参考图编辑）：POST /v1/images/edits?async=true（multipart）
   *
   * 参考图支持 dataURL（本地上传）或 http(s) URL。
   * 复用 pollTask 异步轮询。修复：此前图生图分支只打日志就退回文生图，
   * 参考图被完全丢弃（用户反馈"参考图无法生成"的根因）。
   */
  async generateImageEdit(request: ImageGenerationRequest, refImage: string): Promise<ImageGenerationResult> {
    const startTime = Date.now();
    const model = request.model || 'gpt-image-2';
    try {
      // 参考图 → Blob（dataURL 或 URL）
      let blob: Blob;
      if (refImage.startsWith('data:')) {
        const m = refImage.match(/^data:([^;,]+)?(;base64)?,(.*)$/s);
        const mime = (m?.[1] || 'image/png').split(';')[0];
        if (m?.[2]) {
          const bin = atob(m[3]);
          const arr = new Uint8Array(bin.length);
          for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
          blob = new Blob([arr], { type: mime });
        } else {
          blob = new Blob([decodeURIComponent(m?.[3] || '')], { type: mime });
        }
      } else {
        const r = await fetch(refImage);
        if (!r.ok) return { success: false, images: [], durationMs: Date.now() - startTime, error: `参考图下载失败: HTTP ${r.status}`, model };
        blob = await r.blob();
      }

      const form = new FormData();
      form.append('image', blob, 'reference.png');
      form.append('model', model);
      form.append('prompt', request.prompt);
      form.append('n', String(request.n || 1));
      form.append('size', request.size || '1024x1024');

      this.logger.info(`[ImageGenerationService] 提交图生图任务（异步）: ${model} / 参考图 ${blob.size}B`);

      const createResp = await fetch(`${this.apiBase}/images/edits?async=true`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${this.apiKey}` },
        body: form, // 浏览器自动设置 multipart Content-Type（含 boundary）
      });

      if (!createResp.ok) {
        const errText = await createResp.text().catch(() => '');
        return {
          success: false,
          images: [],
          durationMs: Date.now() - startTime,
          error: `图生图 API 返回 ${createResp.status}: ${errText || createResp.statusText}`,
          model,
        };
      }

      const createData = await createResp.json();
      const taskId = createData?.id;
      if (!taskId) {
        const directImages = (createData?.data || []).map((item: any) => ({ url: item.url, b64Json: item.b64_json, revisedPrompt: item.revised_prompt }));
        if (directImages.length > 0) {
          return { success: true, images: directImages, durationMs: Date.now() - startTime, model };
        }
        return {
          success: false,
          images: [],
          durationMs: Date.now() - startTime,
          error: `图生图任务创建失败：响应缺少 id。${JSON.stringify(createData).slice(0, 200)}`,
          model,
        };
      }

      const pollResult = await this.pollTask(taskId);
      const durationMs = Date.now() - startTime;
      if (!pollResult.success) {
        return { success: false, images: [], durationMs, error: pollResult.error || '图生图失败', model };
      }
      return { success: true, images: [{ url: pollResult.imageUrl }], durationMs, model };
    } catch (err: any) {
      return {
        success: false,
        images: [],
        durationMs: Date.now() - startTime,
        error: err?.message || String(err),
        model,
      };
    }
  }

  /**
   * 生成文章封面（竖版，适合公众号头图/抖音封面）
   *
   * @param title 文章标题
   * @param description 内容描述
   * @param style 视觉风格（如 "科技感深色背景" / "极简白色背景"）
   */
  async generateCover(
    title: string,
    description?: string,
    style?: string
  ): Promise<ImageGenerationResult> {
    const prompt = this.buildCoverPrompt(title, description, style, 'portrait');
    return this.generateImage({
      prompt,
      model: 'gpt-image-2',
      size: '1024x1792',
    });
  }

  /**
   * 生成公众号配图（横版 1792x1024）
   */
  async generateWeChatCover(
    title: string,
    description?: string,
    style?: string
  ): Promise<ImageGenerationResult> {
    const prompt = this.buildCoverPrompt(title, description, style, 'landscape');
    return this.generateImage({
      prompt,
      model: 'gpt-image-2',
      size: '1792x1024',
    });
  }

  /**
   * 生成小红书九宫格（9 张方图）
   *
   * 分 3 次调用，每次生成 3 张（gpt-image-2 每次 n=1，所以调 9 次）
   * 为加速，并发调用
   */
  async generateXiaohongshuGrid(
    topic: string,
    style?: string
  ): Promise<ImageGenerationResult> {
    const prompts = this.buildXiaohongshuGridPrompts(topic, style);
    // 并发生成 9 张图
    const results = await Promise.all(
      prompts.map((prompt) =>
        this.generateImage({
          prompt,
          model: 'gpt-image-2',
          size: '1024x1024',
        })
      )
    );

    const allImages: ImageGenerationResult['images'] = [];
    let firstError: string | undefined;
    let totalDuration = 0;

    for (const r of results) {
      totalDuration = Math.max(totalDuration, r.durationMs);
      if (r.success) {
        allImages.push(...r.images);
      } else if (!firstError) {
        firstError = r.error;
      }
    }

    return {
      success: allImages.length > 0,
      images: allImages,
      durationMs: totalDuration,
      error: allImages.length === 0 ? firstError : undefined,
      model: 'gpt-image-2',
    };
  }

  /**
   * 生成抖音封面（竖版 1024x1792）
   */
  async generateDouyinCover(
    title: string,
    style?: string
  ): Promise<ImageGenerationResult> {
    const prompt = `为抖音短视频生成一张竖版封面。标题：${title}。${style || '科技感深色背景，霓虹蓝绿色调，未来主义风格，文字居中，视觉冲击力强'}。画面干净，不要过多文字，适合移动端竖屏浏览。`;
    return this.generateImage({
      prompt,
      model: 'gpt-image-2',
      size: '1024x1792',
    });
  }

  /**
   * 自定义图片生成（供「图片类」分类生成面板使用）
   * 支持：比例（横屏/竖屏/方图）、清晰度（1K/2K/4K）、模式（文生图/图生图）、参考图（图生图）
   */
  async generateCustom(
    prompt: string,
    opts: { ratio?: string; clarity?: string; mode?: string; refImage?: string; model?: string; n?: number }
  ): Promise<ImageGenerationResult> {
    const ratio = opts?.ratio || '方图';
    const mode = opts?.mode || '文生图';
    const refImage = opts?.refImage || '';
    // 比例 → 尺寸：横屏 1792x1024 / 竖屏 1024x1792 / 方图 1024x1024
    const size = ratio === '横屏' ? '1792x1024' : ratio === '竖屏' ? '1024x1792' : '1024x1024';
    const request: ImageGenerationRequest = {
      prompt,
      model: opts?.model || 'gpt-image-2',
      n: opts?.n ?? 1,
      size: size as ImageGenerationRequest['size'],
    };
    // v6.1.12 图生图：真正实现（/v1/images/edits 带参考图），修复此前参考图被丢弃
    if (mode === '图生图' && refImage) {
      return this.generateImageEdit(request, refImage);
    }
    return this.generateImage(request);
  }

  /**
   * 构建封面图提示词
   */
  private buildCoverPrompt(
    title: string,
    description?: string,
    style?: string,
    orientation?: 'portrait' | 'landscape'
  ): string {
    const layout = orientation === 'portrait' ? '竖版构图' : '横版构图';
    const defaultStyle = '现代专业风格，色彩与内容主题协调，高质感';
    return `生成一张${layout}的文章封面图。

标题：${title}
${description ? `内容摘要：${description}` : ''}
视觉风格：${style || defaultStyle}

要求（重要）：
- 画面干净专业，适合企业级内容
- 不要在图片中渲染中文文字（避免乱码）
- 【必须根据标题与内容摘要的主题，生成具体、相关的视觉元素】——标题/摘要提到什么行业、场景、对象（如酒店、工厂、AI、手机、餐厅、办公室等），就画出对应的具体画面（场景/物品/人物/科技符号等）；【禁止】用千篇一律的抽象渐变、光效背景充当封面
- 每张封面必须与文章主题强相关，不同文章的封面视觉上应有明显差异
- ${layout}，视觉层次分明`;
  }

  /**
   * 构建小红书九宫格提示词（9 张主题相关的方图）
   */
  private buildXiaohongshuGridPrompts(topic: string, style?: string): string[] {
    const baseStyle = style || '明亮柔和的色调，极简风格，适合小红书分享';
    const angles = [
      `主题概述：用一张图表达「${topic}」的核心概念`,
      `问题场景：展示「${topic}」解决的痛点`,
      `解决方案：用图表达「${topic}」的解决思路`,
      `关键步骤1：展示「${topic}」的第一个要点`,
      `关键步骤2：展示「${topic}」的第二个要点`,
      `关键步骤3：展示「${topic}」的第三个要点`,
      `效果对比：用图表达「${topic}」使用前后的变化`,
      `最佳实践：展示「${topic}」的最佳应用场景`,
      `总结升华：用一张图总结「${topic}」的价值`,
    ];
    return angles.map((angle) => `${angle}。视觉风格：${baseStyle}。1024x1024 方图，干净简洁，不要中文文字。`);
  }
}

/** 单例 */
let imageServiceInstance: ImageGenerationService | null = null;

/** 获取图片生成服务单例 */
export function getImageGenerationService(): ImageGenerationService {
  if (!imageServiceInstance) {
    imageServiceInstance = new ImageGenerationService();
  }
  return imageServiceInstance;
}

export default ImageGenerationService;
